import { JsonObject, JsonProperty } from 'json2typescript';
import { Moment } from 'moment';

import {
    ActivityAge,
    ClientRelation,
    ClientStatus,
    ClientType,
    DocumentType,
    DocumentTypeId,
    FatcaInterface,
    FullClientInterface,
    LegalSourceIncome,
    Volume,
} from '../../../core';
import { ActivityAgeAdapter } from '../../client/helpers/serializer/adapters/activity-age.adapter';
import { Address } from './address';
import { BooleanInverterAdapter } from '../../client/helpers/serializer/adapters/boolean-inverter.adapter';
import { Client } from './client';
import { CountryClient } from './country-client';
import { DateAdapter } from '../../client/helpers/serializer/adapters/date.adapter';
import { Document } from './document';
import { Fatca } from './fatca';
import { LegalSourceIncomeAdapter } from '../../client/helpers/serializer/adapters/legal-source-income.adapter';
import { Manager } from './manager';
import { Professional } from './professional';
import { PublicDuties } from './public-duties';
import { StringAdapter } from '../../client/helpers/serializer/adapters/string.adapter';
import { Tax } from './tax';
import { Validation } from './validation';

@JsonObject('FullClient')
export class FullClient extends Client implements FullClientInterface {
    @JsonProperty('IsSuitable', Boolean, true)
    private _isSuitable: boolean = undefined;

    @JsonProperty('IsFavourite', Boolean, true)
    private _isFavourite: boolean = undefined;

    @JsonProperty('TotalBalance', Number, true)
    private _balance: number = undefined;

    @JsonProperty('Currency', StringAdapter, true)
    private _currency: string = undefined;

    @JsonProperty('CityOfBirth', StringAdapter, true)
    private _cityOfBirth: string = undefined;

    @JsonProperty('CountryOfBirth', StringAdapter, true)
    private _countryOfBirth: string = undefined;

    @JsonProperty('HabitualResidence', Address, true)
    private _habitualResidence: Address = undefined;

    @JsonProperty('LegalResidence', Address, true)
    private _legalResidence: Address = undefined;

    @JsonProperty('MailResidence', Address, true)
    private _mailResidence: Address = undefined;

    @JsonProperty('IsDifferentMailAddress', BooleanInverterAdapter, true)
    private _isEqualMailAddress: boolean = undefined;

    @JsonProperty('ProfessionalData', Professional, true)
    private _professional: Professional = undefined;

    @JsonProperty('PerformancePublicDuties', PublicDuties, true)
    private _publicDuties: PublicDuties = undefined;

    @JsonProperty('TaxResidence', Tax, true)
    private _tax: Tax = undefined;

    @JsonProperty('EconomicActivitieCode', StringAdapter, true)
    private _activityCode: string = undefined;

    @JsonProperty('ActivityLength', ActivityAgeAdapter, true)
    private _activityAge: ActivityAge = undefined;

    @JsonProperty('SourceOfIncome', LegalSourceIncomeAdapter, true)
    private _sourceOfIncome: LegalSourceIncome = undefined;

    @JsonProperty('SourceOfIncomeFreeText', StringAdapter, true)
    private _sourceOfIncomeFreeText: string = undefined;

    @JsonProperty('BusinessVolume', StringAdapter, true)
    private _volume: Volume = undefined;

    @JsonProperty('Managers', [Manager], true)
    private _managers: Manager[] = undefined;

    @JsonProperty('FATCAData', Fatca, true)
    private _fatca: Fatca = undefined;

    @JsonProperty('FATCACRSData', [Fatca], true)
    private _fatcas: FatcaInterface[] = undefined;

    @JsonProperty('CompanyConstitutionDate', DateAdapter, true)
    private _companyConstitutionDate: Moment = undefined;

    @JsonProperty('AdditionalFiscalCountries', [CountryClient], true)
    private _additionalFiscalCountries: CountryClient[] = undefined;

    @JsonProperty('HasRiskActivity', Boolean, true)
    private _hasRiskActivity: boolean = undefined;

    @JsonProperty('RiskActivityCode', StringAdapter, true)
    private _riskActivityCode: string = undefined;

    @JsonProperty('ActivityWithoutIncome', StringAdapter, true)
    private _activityWithoutIncome: string = undefined;

    @JsonProperty('ActivityWithoutIncomeOthers', StringAdapter, true)
    private _activityWithoutIncomeOthers: string = undefined;

    @JsonProperty('RiskLevel', StringAdapter, true)
    private _riskLevel: string = undefined;



    private _documents: Document[] = [];
    private _documentsInversis: Document[] = [];
    private _documentsOriginFunds: Document[] = [];
    private _validation: Validation = undefined;
    private _hasAdditionalFiscalCountries: boolean = undefined;
    private _uploadMultipleEnabled = undefined;

    get isSuitable(): boolean {
        return this._isSuitable;
    }

    get isFavourite(): boolean {
        return this._isFavourite;
    }

    get balance(): number {
        return this._balance;
    }

    get currency(): string {
        return this._currency;
    }

    get cityOfBirth(): string {
        return this._cityOfBirth;
    }

    set cityOfBirth(value: string) {
        this._cityOfBirth = value;
    }

    get countryOfBirth(): string {
        return this._countryOfBirth;
    }

    set countryOfBirth(value: string) {
        this._countryOfBirth = value;
    }

    get habitualResidence(): Address {
        if (!this._habitualResidence) {
            this._habitualResidence = new Address();
        }

        return this._habitualResidence;
    }

    get legalResidence(): Address {
        if (!this._legalResidence) {
            this._legalResidence = new Address();
        }

        return this._legalResidence;
    }

    get mailResidence(): Address {
        if (!this._mailResidence) {
            this._mailResidence = new Address();
        }

        return this._mailResidence;
    }

    set mailResidence(value: Address) {
        this._mailResidence = value;
    }

    get isEqualMailAddress(): boolean {
        if (this._isEqualMailAddress === undefined) {
            if (this.type === ClientType.LEGAL_PERSON) {
                this._isEqualMailAddress = this.legalResidence.equals(this.mailResidence);
            } else {
                this._isEqualMailAddress = this.habitualResidence.equals(this.mailResidence);
            }
        }

        return this._isEqualMailAddress;
    }

    set isEqualMailAddress(value: boolean) {
        this._isEqualMailAddress = value;
    }

    get professional(): Professional {
        if (!this._professional) {
            this._professional = new Professional();
        }

        return this._professional;
    }

    get publicDuties(): PublicDuties {
        if (!this._publicDuties) {
            this._publicDuties = new PublicDuties();
        }

        return this._publicDuties;
    }

    get tax(): Tax {
        if (!this._tax) {
            this._tax = new Tax();
        }

        return this._tax;
    }

    get activityCode(): string {
        return this._activityCode;
    }

    set activityCode(value: string) {
        this._activityCode = value;
    }

    get activityAge(): ActivityAge {
        return this._activityAge;
    }

    set activityAge(value: ActivityAge) {
        this._activityAge = value;
    }

    get sourceOfIncome(): LegalSourceIncome {
        return this._sourceOfIncome;
    }

    set sourceOfIncome(value: LegalSourceIncome) {
        this._sourceOfIncome = value;
    }

    get sourceOfIncomeFreeText(): string {
        return this._sourceOfIncomeFreeText;
    }

    set sourceOfIncomeFreeText(value: string) {
        this._sourceOfIncomeFreeText = value;
    }

    get volume(): Volume {
        return this._volume;
    }

    set volume(value: Volume) {
        this._volume = value;
    }

    get managers(): Manager[] {
        if (!this._managers) {
            this._managers = [];
        }

        return this._managers;
    }

    get hasRiskActivity(): boolean {
        return this._hasRiskActivity;
    }

    set hasRiskActivity(value: boolean) {
        this._hasRiskActivity = value;
    }

    get riskActivityCode(): string {
        return this._riskActivityCode;
    }

    set riskActivityCode(value: string) {
        this._riskActivityCode = value;
    }

    get activityWithoutIncome(): string {
        return this._activityWithoutIncome;
    }

    set activityWithoutIncome(value: string) {
        this._activityWithoutIncome = value;
    }

    get activityWithoutIncomeOthers(): string {
        return this._activityWithoutIncomeOthers;
    }

    set activityWithoutIncomeOthers(value: string) {
        this._activityWithoutIncomeOthers = value;
    }

    get riskLevel(): string {
        return this._riskLevel;
    }

    set riskLevel(value: string) {
        this._riskLevel = value;
    }

    addManager(value: Manager) {
        if (!this.managers.find((it) => String(it.id) === String(value.id))) {
            this.managers.push(value);
        }
    }

    updateManager(value: Manager) {
        this.managers.map((it, idx) => {
            if (String(it.id) === String(value.id)) {
                this.managers[idx] = value;
            }
        });
    }

    removeManager(value: Manager) {
        this.managers.map((it, idx) => {
            if (String(it.id) === String(value.id)) {
                this.managers.splice(idx, 1);
            }
        });
    }

    get fatca(): Fatca {
        return this._fatca;
    }

    get fatcas(): FatcaInterface[] {
        if (!this._fatcas) {
            this._fatcas = [];
        }
        return this._fatcas;
    }

    get documents(): Document[] {
        if (!this._documents) {
            this._documents = [];
        }

        return this._documents;
    }

    set documents(value: Document[]) {
        this._documents = value;
    }

    get documentsInversis(): Document[] {
        if (!this._documentsInversis) {
            this._documentsInversis = [];
        }

        return this._documentsInversis;
    }

    set documentsInversis(value: Document[]) {
        this._documentsInversis = value;
    }

    get documentsOriginFunds(): Document[] {
        if (!this._documentsOriginFunds) {
            this._documentsOriginFunds = [];
        }

        return this._documentsOriginFunds;
    }

    set documentsOriginFunds(value: Document[]) {
        this._documentsOriginFunds = value;
    }

    get allowMultipleUploadDocument(): boolean {
        return this.docType === DocumentType.DNI || this.docType === DocumentType.NIE || this.docType === DocumentType.NIE_NOT_RESIDENT;
    }

    set uploadMultipleEnabled(enabled: boolean) {
        if (enabled) {
            const identifyDocument = this.documents.find(
                (it) =>
                    it.typeId.value === DocumentTypeId.DNI ||
                    it.typeId.value === DocumentTypeId.NIE ||
                    it.typeId.value === DocumentTypeId.NIE_NOT_RESIDENT
            );
            if (identifyDocument) {
                identifyDocument.file = undefined;
            }
        } else {
            this.uploadMultipleDocuments.forEach((it) => {
                it.file = undefined;
            });
        }
        this._uploadMultipleEnabled = enabled;
    }

    get uploadMultipleEnabled(): boolean {
        if (this._uploadMultipleEnabled === undefined) {
            const identifyDocument = this._documents.find(
                (it) =>
                    it.typeId.value === DocumentTypeId.DNI ||
                    it.typeId.value === DocumentTypeId.NIE ||
                    it.typeId.value === DocumentTypeId.NIE_NOT_RESIDENT
            );
            let frontDocument: Document;
            let backDocument: Document;

            switch (String(identifyDocument?.typeId)) {
                case DocumentTypeId.DNI:
                    frontDocument = this._documents.find((it) => it.typeId.value === DocumentTypeId.DNI_FRONT_SIDE);
                    backDocument = this._documents.find((it) => it.typeId.value === DocumentTypeId.DNI_BACK_SIDE);
                    break;
                case DocumentTypeId.NIE:
                    frontDocument = this._documents.find((it) => it.typeId.value === DocumentTypeId.NIE_FRONT_SIDE);
                    backDocument = this._documents.find((it) => it.typeId.value === DocumentTypeId.NIE_BACK_SIDE);
                    break;
                case DocumentTypeId.NIE_NOT_RESIDENT:
                    frontDocument = this._documents.find((it) => it.typeId.value === DocumentTypeId.NIE_NOT_RESIDENT_FRONT_SIDE);
                    backDocument = this._documents.find((it) => it.typeId.value === DocumentTypeId.NIE_NOT_RESIDENT_BACK_SIDE);
                    break;
            }

            this.uploadMultipleEnabled = !!frontDocument?.id || !!backDocument?.id;
        }

        return this._uploadMultipleEnabled;
    }

    get uploadMultipleDocuments(): Document[] {
        return this.documents.filter(
            (doc) =>
                doc.typeId.value === DocumentTypeId.DNI_FRONT_SIDE ||
                doc.typeId.value === DocumentTypeId.DNI_BACK_SIDE ||
                doc.typeId.value === DocumentTypeId.NIE_FRONT_SIDE ||
                doc.typeId.value === DocumentTypeId.NIE_BACK_SIDE ||
                doc.typeId.value === DocumentTypeId.NIE_NOT_RESIDENT_FRONT_SIDE ||
                doc.typeId.value === DocumentTypeId.NIE_NOT_RESIDENT_BACK_SIDE
        );
    }

    get validation(): Validation {
        if (!this._validation) {
            this._validation = new Validation(this.id);
        }

        return this._validation;
    }

    set validation(value: Validation) {
        this._validation = value;
    }

    get companyConstitutionDate(): Moment {
        return this._companyConstitutionDate;
    }

    set companyConstitutionDate(value: Moment) {
        this._companyConstitutionDate = value;
    }

    get additionalFiscalCountries(): CountryClient[] {
        if (!this._additionalFiscalCountries) {
            this._additionalFiscalCountries = [];
        }

        return this._additionalFiscalCountries;
    }

    set additionalFiscalCountries(value: CountryClient[]) {
        this._additionalFiscalCountries = value;
    }

    get hasAdditionalFiscalCountries(): boolean {
        if (this._hasAdditionalFiscalCountries === undefined) {
            this._hasAdditionalFiscalCountries = !!this._additionalFiscalCountries && this._additionalFiscalCountries.length > 0;
        }

        return this._hasAdditionalFiscalCountries;
    }

    set hasAdditionalFiscalCountries(value: boolean) {
        this._hasAdditionalFiscalCountries = value;
    }

    isPersonalDataComplete(): boolean {
        switch (this.type) {
            case ClientType.DISABLED:
            case ClientType.MINOR:
            case ClientType.NATURAL_PERSON:
                return (
                    !!this.name &&
                    !!this.surname &&
                    !!this.citizenship &&
                    !!this.countryOfResidence &&
                    !!this.countryOfBirth &&
                    !!this.cityOfBirth &&
                    !!this.birthDate &&
                    this.habitualResidence.isComplete() &&
                    this.mailResidence.isComplete()
                );
            case ClientType.LEGAL_PERSON:
                return (
                    !!this.companyConstitutionDate &&
                    !!this.activityCode &&
                    !!this.activityAge &&
                    !!this.sourceOfIncome &&
                    (this.sourceOfIncome === LegalSourceIncome.OTHER ? !!this.sourceOfIncomeFreeText : true) &&
                    !!this.volume &&
                    this.legalResidence.isComplete() &&
                    this.mailResidence.isComplete()
                );
            default:
                return false;
        }
    }

    isProfessionalDataComplete(isGDC = false): boolean {
        if (isGDC){
            if (this.relation === ClientRelation.AUTHORIZED){
                return true;
            }
            else{
                return this.professional.isComplete() && this.professional.isCompleteInversis();
            }
        }
        else {
            return this.professional.isComplete();
        }
    }

    isShareHoldersDataComplete(): boolean {
        return (this.hasMainShareHolders || this.hasSecondaryShareHolders ? this.shareHolders.length > 0 && !this.shareHolders.some(shareHolder => !shareHolder.isComplete())
            : (this.hasMainShareHolders === false && this.hasSecondaryShareHolders === false));
    }

    isPublicDutiesDataComplete(): boolean {
        return this.publicDuties.isComplete();
    }

    isManagersDataComplete(): boolean {
        return this.managers.length > 0 && !this.managers.some((manager) => !manager.isComplete());
    }

    isTaxDataComplete(): boolean {
        return this.tax.isComplete();
    }

    isFatcaDataComplete(hasFullManagers = false): boolean {
        return this.fatca.isComplete(hasFullManagers);
    }

    isDocumentsDataComplete(): boolean {
        if (this.allowMultipleUploadDocument && this.uploadMultipleEnabled) {
            const notIdentifyDocuments = this._documents.filter(
                (it) =>
                    it.typeId.value !== DocumentTypeId.DNI &&
                    it.typeId.value !== DocumentTypeId.NIE &&
                    it.typeId.value !== DocumentTypeId.NIE_NOT_RESIDENT
            );
            const multipleUploadDocuments = this.uploadMultipleDocuments;
            return (
                !notIdentifyDocuments.some((document) => !document.isComplete()) &&
                !multipleUploadDocuments.some((document) => !document.isComplete())
            );
        } else {
            const multipleUploadDocuments = this.uploadMultipleDocuments;
            return (
                this.documents &&
                !this.documents.filter((document) => !multipleUploadDocuments.includes(document)).some((document) => !document.isComplete())
            );
        }
    }

    isComplete(isGDC = false): boolean {
        if (this.isValidated()) {
            switch (this.type) {
                case ClientType.NATURAL_PERSON:
                    if (isGDC){
                            return this.isProfessionalDataComplete(isGDC) && this.isDocumentsDataComplete();
                    }
                    else {
                        if(this.relation !== ClientRelation.AUTHORIZED){
                            return this.isProfessionalDataComplete(isGDC) && this.isDocumentsDataComplete();
                        }
                        else{
                            return this.isDocumentsDataComplete();
                        }
                    }
                default:
                    return this.isDocumentsDataComplete();
            }
        }
        switch (this.type) {
            case ClientType.DISABLED:
            case ClientType.MINOR:
                return this.isPersonalDataComplete() && this.isDocumentsDataComplete() && this.isProfessionalDataComplete(isGDC) && this.isPublicDutiesDataComplete();
            case ClientType.NATURAL_PERSON:
                if (this.relation === ClientRelation.AUTHORIZED && !this.legalGuardianType) {
                    return this.isPersonalDataComplete()
                        && this.isDocumentsDataComplete();
                } else {
                return (
                    this.isPersonalDataComplete() &&
                    this.isProfessionalDataComplete(isGDC) &&
                    this.isPublicDutiesDataComplete() &&
                    (this.isResident ? true : this.isTaxDataComplete()) &&
                    this.isDocumentsDataComplete()
                );
                }
            case ClientType.LEGAL_PERSON:
                return (
                    this.isPersonalDataComplete() &&
                    this.isShareHoldersDataComplete() &&
                    this.isManagersDataComplete() &&
                    this.isFatcaDataComplete(true) &&
                    this.isDocumentsDataComplete()
                );
            default:
                return false;
        }
    }

     isValidated(isPBC = false): boolean {
        return (
            (this.status === ClientStatus.VALIDATED || (this.status === ClientStatus.CLIENT && localStorage.getItem('IsTitular') !== 'true')) &&
            !this.documents.some((document) => !!document.id && !document.isValidated()) &&
            (isPBC ? !this.hasPBCPending : true)
        );
    }
}
